import { type Axis, type Options } from 'highcharts';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useFlareContext } from '@/components/Flare/FlareContext';
import { type FlareChart } from '@/components/Flare/types';
import { cn } from '@/lib/utils';

export type MarkerDataType = {
  id: string;
  label: string;
  align: 'center' | 'right' | 'left';
  date: string;
};

type Props = {
  data: MarkerDataType[];
  renderLabel?: (data: MarkerDataType) => ReactNode;
};

const buildMarkerOptions = (
  options: Options,
  onChartRedraw: (chart: FlareChart) => void,
): Options => {
  return {
    ...options,
    chart: {
      ...options.chart,
      events: {
        ...options.chart?.events,
        redraw: function (event) {
          options.chart?.events?.redraw?.call(this, event);
          onChartRedraw(this as FlareChart);
        },
      },
    },
  };
};

const Marker = ({ data, renderLabel = (marker) => marker.label }: Props) => {
  const { id, registerChild, parentProps } = useFlareContext();
  const [xAxis, setXAxis] = useState<Axis | undefined>();
  const [plotBounds, setPlotBounds] = useState<{
    plotTop: number;
    plotLeft: number;
    plotHeight: number;
    plotWidth: number;
  }>();
  const [container, setContainer] = useState<Element | null>(null);
  const { parseX, data: parentData, isLoading } = parentProps;

  const handleRender = useCallback((chart: FlareChart) => {
    if (chart) {
      setXAxis(chart.xAxis[0]);
      setPlotBounds({
        plotTop: chart.plotTop,
        plotLeft: chart.plotLeft,
        plotHeight: chart.plotHeight,
        plotWidth: chart.plotWidth,
      });
      setContainer(chart.container?.parentNode as Element);
    }
  }, []);

  useEffect(() => {
    registerChild(id, (options: Options) => buildMarkerOptions(options, handleRender));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleRender]);

  if (!container || !container.parentNode || isLoading || !data || !parentData || !plotBounds) {
    return null;
  }

  const { plotTop, plotLeft, plotHeight } = plotBounds;

  return createPortal(
    <div className="absolute top-0 z-1">
      {data?.map((marker) => {
        const xPos = xAxis?.toPixels(parseX ? parseX(marker.date) : marker.date, true) ?? NaN;
        const left = Math.round(xPos + plotLeft);

        if (isNaN(left)) {
          return null;
        }

        return (
          <div
            key={marker.id}
            className="pointer-events-none absolute -translate-x-1/2"
            style={{
              left,
              top: plotTop - 5,
              height: plotHeight + 5,
            }}
          >
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2">
              <svg xmlns="http://www.w3.org/2000/svg" height="100%" width="2px">
                <line
                  className="stroke-gray-900 stroke-[3px] [stroke-dasharray:8_6]"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="100%"
                />
              </svg>
            </div>
            <div
              className={cn(
                'pointer-events-auto relative whitespace-nowrap rounded-sm bg-gray-900 px-6 py-3 text-base font-semibold leading-5 tracking-[-0.03em] text-white',
                marker.align === 'left' && '-translate-y-full translate-x-[40%]',
                marker.align === 'center' && '-translate-y-full',
                marker.align === 'right' && '-translate-y-full translate-x-[-40%]',
              )}
            >
              {renderLabel(marker)}
            </div>
          </div>
        );
      })}
    </div>,
    container,
  );
};

export default Marker;
